<template>
    <div>
        <Navigation/>
        <HeroSection/>
        <GridView/>

        <!-- Nearby Cities Section — above footer -->
        <section v-if="nearbyCities.length" class="nearby-section">
            <div class="nearby-container">
                <div class="nearby-header">
                    <span class="nearby-pin">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="currentColor"/>
                        </svg>
                    </span>
                    <div>
                        <h2 class="nearby-title">Explore Nearby Cities</h2>
                        <p class="nearby-subtitle">
                            Browse listings in cities near
                            <strong class="nearby-location-name">{{ searchLocationName || 'your location' }}</strong>
                        </p>
                    </div>
                </div>
                <div class="nearby-chips">
                    <a
                        v-for="city in nearbyCities.slice(0, 50)"
                        :key="city._id"
                        :href="city.pageName"
                        class="nearby-chip"
                    >
                        <span class="chip-city">{{ city.city }}</span>
                        <span class="chip-state">{{ city.state }}</span>
                        <span class="chip-divider">·</span>
                        <span class="chip-type">{{ formatSubtype(city.pageName) }}</span>
                    </a>
                </div>
            </div>
        </section>

        <!-- Why Renants — 3-point closing pitch above the footer -->
        <ValueProps/>

        <FooterSection/>
    </div>
</template>
<script>

import Navigation from './Navigation.vue';
import HeroSection from './HeroSection.vue';
import GridView from './GridView.vue';
import ValueProps from './ValueProps.vue';
import FooterSection from './footer.vue';
import axiosInst from '@/utils/axiosinstance';
import { EventBus } from '@/services/EventBus.js';
import { getIPLocation } from '@/services/ipLocationService.js';
import { captureCoords, setUserLocation } from '@/services/userLocationService.js';
import { getCachedCoords, setCachedCoords } from '@/utils/geocodeCache.js';

export default {
    components: {
        Navigation,
        HeroSection,
        GridView,
        ValueProps,
        FooterSection
    },
    data() {
        return {
            nearbyCities: [],
            nearbyError: '',
            selectedSubType: '',
            selectedServiceType: '',
            searchLat: null,
            searchLng: null,
            searchLocationName: '',
            _nearbyFetchPending: false,
            // Monotonic counter so an older in-flight nearby fetch can't overwrite
            // the result of a newer one (e.g. the empty-filter fetch resolving
            // after a Movies-filtered fetch and clobbering the Movies chips).
            _nearbyFetchSeq: 0
        };
    },
    methods: {
        // Coalesce multiple EventBus watcher triggers into one nearby-cities fetch.
        scheduleNearbyFetch() {
            if (this._nearbyFetchPending) return;
            this._nearbyFetchPending = true;
            this.$nextTick(() => {
                if (this._nearbyFetchPending) {
                    this._nearbyFetchPending = false;
                    if (this.searchLat != null && this.searchLng != null) {
                        this._fetchNearbyCitiesWithCoords(this.searchLat, this.searchLng);
                    }
                }
            });
        },
        formatSubtype(pageName) {
            // pageName format: /find-a-room/bensalem-township → "Find a Room"
            if (!pageName) return '';
            const parts = pageName.split('/');
            if (parts.length < 2) return '';
            return parts[1]
                .replace(/-/g, ' ')
                .replace(/\b\w/g, c => c.toUpperCase());
        },

        // Geocode a location string (e.g. "New York, NY") to lat/lng, then fetch nearby cities.
        // Only one definition — the duplicate below was removed (it overrode this one and
        // dropped the null-guard and the searchLocationName assignment).
        async _geocodeAndFetch(locationStr) {
            if (!locationStr || typeof locationStr !== 'string' || !locationStr.trim()) return;
            const cached = getCachedCoords(locationStr);
            if (cached) {
                this.searchLat = cached.lat;
                this.searchLng = cached.lng;
                this.searchLocationName = locationStr.trim();
                await this._fetchNearbyCitiesWithCoords(cached.lat, cached.lng);
                return;
            }
            try {
                const { data: autocompleteData } = await axiosInst.post('/api/places/autocomplete', { input: locationStr });
                const placeID = autocompleteData[0]?.place_id;
                if (placeID) {
                    const { data: detailsData } = await axiosInst.post('/api/placedetails', { placeId: placeID });
                    const lat = detailsData.result?.geometry?.location?.lat || detailsData.lat;
                    const lng = detailsData.result?.geometry?.location?.lng || detailsData.lng;
                    if (lat && lng) {
                        setCachedCoords(locationStr, lat, lng);
                        this.searchLat = lat;
                        this.searchLng = lng;
                        this.searchLocationName = locationStr.trim();
                        await this._fetchNearbyCitiesWithCoords(lat, lng);
                        return;
                    }
                }
            } catch (e) {
                // geocoding failed, fall through to IP/geolocation
            }
            await this.fetchNearbyCities();
        },

        async fetchNearbyCities() {
            this.nearbyError = '';
            const lat = this.searchLat;
            const lng = this.searchLng;

            // ── Fast path: coords already resolved ────────────────────────────
            // This fires when location comes from Views.vue {lat,lng,label} object
            // or when the user picks a city. Skip all geo/IP fallbacks.
            if (lat != null && lng != null) {
                await this._fetchNearbyCitiesWithCoords(lat, lng);
                return;
            }

            // ── No coords: try browser geolocation ────────────────────────────
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(async (pos) => {
                    // Discard GPS result if a real location arrived while we waited
                    if (this.searchLat != null && this.searchLng != null) return;
                    this.searchLat = pos.coords.latitude;
                    this.searchLng = pos.coords.longitude;
                    // Share the granted location so Deals / More default to it
                    // instead of prompting again or starting at "All states".
                    captureCoords(this.searchLat, this.searchLng);
                    await this._fetchNearbyCitiesWithCoords(this.searchLat, this.searchLng);
                }, async () => {
                    // Geolocation denied → fall back to IP
                    await this._ipFallback();
                });
                return;
            }

            // ── No geolocation support: use IP lookup ─────────────────────────
            await this._ipFallback();
        },

        async _ipFallback() {
            // Guard: if coords arrived while IP lookup was in flight, discard result
            if (this.searchLat != null && this.searchLng != null) return;
            const ipLoc = await getIPLocation();
            if (ipLoc?.lat && ipLoc?.lng) {
                if (this.searchLat != null && this.searchLng != null) return;
                this.searchLat = ipLoc.lat;
                this.searchLng = ipLoc.lng;
                this.searchLocationName = ipLoc.label || ipLoc.city || 'your location';
                // Geolocation was unavailable/denied — share the IP guess so the
                // other pages at least land in the right state.
                setUserLocation(ipLoc);
                await this._fetchNearbyCitiesWithCoords(ipLoc.lat, ipLoc.lng);
                return;
            }
            this.nearbyError = 'Unable to detect location.';
        },

        async _fetchNearbyCitiesWithCoords(lat, lng) {
            if (lat == null || lng == null) return;
            let url = `/api/citywise-metadata/nearby?lat=${lat}&lng=${lng}`;
            const filterType = this.selectedSubType || this.selectedServiceType;
            if (filterType) {
                url += `&serviceType=${encodeURIComponent(filterType)}`;
            }
            const seq = ++this._nearbyFetchSeq;
            try {
                const res = await axiosInst.get(url);
                if (seq !== this._nearbyFetchSeq) return; // stale response — newer fetch in flight
                this.nearbyCities = res.data.data || [];
                if (!this.nearbyCities.length) {
                    this.nearbyError = 'No nearby cities found.';
                } else {
                    this.nearbyError = '';
                }
            } catch (e) {
                if (seq !== this._nearbyFetchSeq) return;
                this.nearbyCities = [];
                this.nearbyError = e.response && e.response.data && e.response.data.message
                    ? e.response.data.message
                    : 'Failed to load nearby cities.';
            }
        },
    },
    mounted() {
        this.nearbyError = '';
        this.selectedSubType = EventBus.selectedSubtype || '';
        this.selectedServiceType = EventBus.selectedServiceType || '';
        // Resolve initial location from EventBus
        const loc = EventBus.selectedLocation;
        if (loc && typeof loc === 'object' && loc.lat && loc.lng) {
            this.searchLat = loc.lat;
            this.searchLng = loc.lng;
            this.searchLocationName = loc.label || '';   // extract label from {lat,lng,label}
            this.fetchNearbyCities();
        } else if (loc && typeof loc === 'string' && loc.trim()) {
            this._geocodeAndFetch(loc);
        } else {
            this.fetchNearbyCities();
        }

        // Watch for changes in EventBus.selectedSubtype and update panel.
        // Only re-fetch directly when coords are already resolved — avoids
        // triggering the geolocation fallback (race condition) while an async
        // _geocodeAndFetch is still in-flight. If no coords yet, just update
        // this.selectedSubType; _geocodeAndFetch will read it when it completes.
        this._eventBusWatcher = this.$watch(
            () => EventBus.selectedSubtype,
            (newVal) => {
                this.selectedSubType = newVal || '';
                this.scheduleNearbyFetch();
            }
        );

        // Watch for category (service type) changes from AdvanceFilter chips
        this._eventBusServiceTypeWatcher = this.$watch(
            () => EventBus.selectedServiceType,
            (newVal) => {
                this.selectedServiceType = newVal || '';
                this.scheduleNearbyFetch();
            }
        );

        // Watch for location changes — handle both string and {lat,lng} object forms
        this._eventBusLocationWatcher = this.$watch(
            () => EventBus.selectedLocation,
            (newLoc) => {
                if (!newLoc) return;
                if (typeof newLoc === 'object' && newLoc.lat && newLoc.lng) {
                    this.searchLat = newLoc.lat;
                    this.searchLng = newLoc.lng;
                    this.searchLocationName = newLoc.label || ''; // extract label from {lat,lng,label}
                    this.fetchNearbyCities();
                } else if (typeof newLoc === 'string' && newLoc.trim()) {
                    this._geocodeAndFetch(newLoc);
                }
            }
        );
    },
    beforeUnmount() {
        if (this._eventBusWatcher) this._eventBusWatcher();
        if (this._eventBusServiceTypeWatcher) this._eventBusServiceTypeWatcher();
        if (this._eventBusLocationWatcher) this._eventBusLocationWatcher();
    }
}
</script>
<style scoped>
/* ── Nearby Cities Section ── */
.nearby-section {
    background: #f4f6fb;
    border-top: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;
    padding: 2.5rem 1rem 2.8rem;
}

.nearby-container {
    max-width: 1200px;
    margin: 0 auto;
}

/* Header row */
.nearby-header {
    display: flex;
    align-items: flex-start;
    gap: 0.85rem;
    margin-bottom: 1.6rem;
}

.nearby-pin {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    min-width: 42px;
    background: linear-gradient(135deg, #1a73e8 0%, #4f8ef7 100%);
    border-radius: 50%;
    color: #fff;
    box-shadow: 0 2px 8px rgba(26, 115, 232, 0.28);
}

.nearby-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1a202c;
    margin: 0 0 0.2rem;
    letter-spacing: -0.01em;
    font-family: 'Segoe UI', 'Inter', 'Roboto', Arial, sans-serif;
}

.nearby-subtitle {
    font-size: 0.875rem;
    color: #64748b;
    margin: 0;
    font-family: 'Segoe UI', 'Inter', 'Roboto', Arial, sans-serif;
}

.nearby-location-name {
    color: #1a73e8;
    font-weight: 600;
    font-style: normal;
}

/* Chips grid */
.nearby-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.55rem;
}

.nearby-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    padding: 0.38rem 0.85rem;
    border-radius: 999px;
    border: 1.5px solid #d1daf0;
    background: #ffffff;
    text-decoration: none;
    font-family: 'Segoe UI', 'Inter', 'Roboto', Arial, sans-serif;
    font-size: 0.82rem;
    color: #374151;
    transition: all 0.18s ease;
    white-space: nowrap;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.nearby-chip:hover {
    background: #1a73e8;
    border-color: #1a73e8;
    color: #ffffff;
    box-shadow: 0 3px 10px rgba(26, 115, 232, 0.25);
    transform: translateY(-1px);
}

.nearby-chip:hover .chip-city,
.nearby-chip:hover .chip-state,
.nearby-chip:hover .chip-divider,
.nearby-chip:hover .chip-type {
    color: inherit;
}

.chip-city {
    font-weight: 600;
    color: #1e293b;
}

.chip-state {
    font-size: 0.78rem;
    color: #64748b;
    font-weight: 400;
}

.chip-divider {
    color: #cbd5e1;
    font-weight: 300;
    font-size: 0.9rem;
}

.chip-type {
    color: #4b5563;
    font-size: 0.78rem;
}

/* Responsive */
@media (max-width: 640px) {
    .nearby-section {
        padding: 1.8rem 1rem 2rem;
    }
    .nearby-title {
        font-size: 1.1rem;
    }
    .nearby-chip {
        font-size: 0.78rem;
        padding: 0.32rem 0.7rem;
    }
}
</style>


